import { Fragment, useEffect, useMemo, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { isNull, debounce } from "lodash"
import { Switch, FormControlLabel } from "@mui/material"

import Divider from "@/components/divider/Divider"
import TextField from "@/components/form/textField/TextField"
import { ScatterChartSettings, SettingSectionProps } from "@/app/report/common/type"
import ColorPicker from "@/app/report/components/setting/ColorPicker"
import MetricSetting from "@/app/report/components/setting/MetricSetting"
import SettingSectionItem from "@/app/report/components/setting/SettingSectionItem"
import { colors } from "@/components/colors"
import DeleteItemDialog from "@/app/report/components/setting/DeleteItemDialog"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"

const DEFAULT_SCATTER_SETTING_ITEMS = [
  {
    xAxis: "totalDistance",
    yAxis: "hsrDistance",
    color: colors.purple[700],
  },
  {
    xAxis: "sprintDistance",
    yAxis: "maxSpeed",
    color: colors.lime[700],
  },
]
const DEFAULT_SCATTER_SETTING_ITEMS_ADD = [
  {
    xAxis: "",
    yAxis: "",
    color: colors.blue[700],
  },
  {
    xAxis: "",
    yAxis: "",
    color: colors.yellow[700],
  },
]

export default function ScatterGraphSection({
  settings,
  tempTemplate,
  setTempTemplate,
  sectionIndex,
}: SettingSectionProps<ScatterChartSettings[]> & { sectionIndex: number }) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const [tempScatterItems, setTempScatterItems] = useState<ScatterChartSettings[]>(
    DEFAULT_SCATTER_SETTING_ITEMS,
  )
  const [deleteItemIndex, setDeleteItemIndex] = useState<number | null>(null)
  const [manualScaleValues, setManualScaleValues] = useState<{
    [itemIndex: number]: { xAxis?: string; yAxis?: string }
  }>({})

  const debouncedUpdateManualScale = useCallback(
    debounce((itemIndex: number, axis: "xAxis" | "yAxis", value: number | null) => {
      setTempScatterItems((prev) => {
        const updated = [...prev]
        updated[itemIndex] = {
          ...updated[itemIndex],
          [`${axis}ManualScaleValue`]: value,
        }

        return updated
      })
    }, 200),
    [],
  )

  useEffect(() => {
    setTempScatterItems(settings)
  }, [settings])

  useEffect(() => {
    setTempTemplate((prev) => {
      if (!prev) return null
      const updatedContent = [...prev.content]
      updatedContent[sectionIndex] = { ...updatedContent[sectionIndex], settings: tempScatterItems }
      return { ...prev, content: updatedContent }
    })
  }, [tempScatterItems, sectionIndex, setTempTemplate])

  const groupedSettings = useMemo(() => {
    const groupedSettings = []
    for (let i = 0; i < tempScatterItems.length; i += 2) {
      groupedSettings.push(tempScatterItems.slice(i, i + 2))
    }

    return groupedSettings
  }, [tempScatterItems])

  return (
    <>
      <div className="DailySettingScatterGraphSectionCont">
        {groupedSettings.map((setting, index) => {
          return (
            <SettingSectionItem
              key={`scatter-setting-item-row-${index}`}
              onDeleteItem={index !== 0 ? () => setDeleteItemIndex(index) : undefined}>
              <div
                className="DailySettingScatterGraphItemCont"
                key={`scatter-setting-item-wrapper-${index}`}>
                {setting.map((item, itemIndex) => {
                  return (
                    <Fragment key={`scatter-setting-content-${itemIndex}`}>
                      <div className="DailySettingScatterGraphContentCont">
                        <div className="daily-setting-scatter-title">
                          <TextField
                            label={t("title")}
                            value={
                              item.xAxis && item.yAxis
                                ? `${translateKey(item.xAxis)} / ${translateKey(item.yAxis)}`
                                : "X-Axis Metric / Y-Axis Metric"
                            }
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                          {/* TODO 컬러픽커 변경 가능성 */}
                          <ColorPicker
                            color={item.color}
                            setColor={(color) =>
                              setTempScatterItems((prev) => {
                                const updated = [...prev]
                                updated[index + (index + itemIndex)] = {
                                  ...updated[index + (index + itemIndex)],
                                  color: color,
                                }

                                return updated
                              })
                            }
                          />
                        </div>
                        <div className="daily-setting-scatter-metric-row">
                          <MetricSetting
                            selectedMetric={item.xAxis}
                            setSelectedMetric={(value) =>
                              setTempScatterItems((prev) => {
                                const updated = [...prev]
                                updated[index + itemIndex] = {
                                  ...updated[index + itemIndex],
                                  xAxis: value,
                                }

                                return updated
                              })
                            }
                            label="X-Axis Metric"
                          />
                          <div className="daily-setting-scatter-manual-scale-wrapper">
                            <p className="daily-setting-scatter-manual-scale-title">
                              {t("manualScale")}
                            </p>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={item.xAxisManualScaleActive || false}
                                  onChange={(e) => {
                                    setTempScatterItems((prev) => {
                                      const updated = [...prev]
                                      updated[index + itemIndex] = {
                                        ...updated[index + itemIndex],
                                        xAxisManualScaleActive: e.target.checked,
                                      }

                                      return updated
                                    })
                                  }}
                                  size="small"
                                />
                              }
                              label=""
                            />
                          </div>
                          <div className="daily-setting-scatter-max-value-wrapper">
                            <p className="daily-setting-scatter-max-value-title">{t("maxValue")}</p>
                            <TextField
                              value={
                                manualScaleValues[index + itemIndex]?.xAxis !== undefined
                                  ? manualScaleValues[index + itemIndex].xAxis
                                  : item.xAxisManualScaleValue || ""
                              }
                              onChange={(e) => {
                                const inputValue = e.target.value

                                if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
                                  return
                                }

                                setManualScaleValues((prev) => ({
                                  ...prev,
                                  [index + itemIndex]: {
                                    ...prev[index + itemIndex],
                                    xAxis: inputValue,
                                  },
                                }))

                                const numValue = inputValue === "" ? null : Number(inputValue)
                                debouncedUpdateManualScale(index + itemIndex, "xAxis", numValue)
                              }}
                              disabled={!item.xAxisManualScaleActive}
                            />
                          </div>
                        </div>
                        <div className="daily-setting-scatter-metric-row">
                          <MetricSetting
                            selectedMetric={item.yAxis}
                            setSelectedMetric={(value) =>
                              setTempScatterItems((prev) => {
                                const updated = [...prev]
                                updated[index + itemIndex] = {
                                  ...updated[index + itemIndex],
                                  yAxis: value,
                                }

                                return updated
                              })
                            }
                            label="Y-Axis Metric"
                          />
                          <div className="daily-setting-scatter-manual-scale-wrapper">
                            <p className="daily-setting-scatter-manual-scale-title">
                              {t("manualScale")}
                            </p>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={item.yAxisManualScaleActive || false}
                                  onChange={(e) => {
                                    setTempScatterItems((prev) => {
                                      const updated = [...prev]
                                      updated[index + itemIndex] = {
                                        ...updated[index + itemIndex],
                                        yAxisManualScaleActive: e.target.checked,
                                      }

                                      return updated
                                    })
                                  }}
                                  size="small"
                                />
                              }
                              label=""
                            />
                          </div>
                          <div className="daily-setting-scatter-max-value-wrapper">
                            <p className="daily-setting-scatter-max-value-title">{t("maxValue")}</p>
                            <TextField
                              value={
                                manualScaleValues[index + itemIndex]?.yAxis !== undefined
                                  ? manualScaleValues[index + itemIndex].yAxis
                                  : item.yAxisManualScaleValue || ""
                              }
                              onChange={(e) => {
                                const inputValue = e.target.value

                                if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
                                  return
                                }

                                setManualScaleValues((prev) => ({
                                  ...prev,
                                  [index + itemIndex]: {
                                    ...prev[index + itemIndex],
                                    yAxis: inputValue,
                                  },
                                }))

                                const numValue = inputValue === "" ? null : Number(inputValue)
                                debouncedUpdateManualScale(index + itemIndex, "yAxis", numValue)
                              }}
                              disabled={!item.yAxisManualScaleActive}
                            />
                          </div>
                        </div>
                      </div>
                      {itemIndex === 0 && <Divider orientation="vertical" />}
                    </Fragment>
                  )
                })}
              </div>
            </SettingSectionItem>
          )
        })}
      </div>

      <DeleteItemDialog
        deleteItemIndex={deleteItemIndex}
        setDeleteItemIndex={setDeleteItemIndex}
        onDeleteAction={(index) =>
          setTempScatterItems((prev) =>
            prev.filter((_, i) => !isNull(index) && i !== index * 2 && i !== index * 2 + 1),
          )
        }
      />
    </>
  )
}
